import {
    GridUIContextActions,
    GridUIContextValue,
} from "features/allocations/contexts/GridUIContext";
import React, { useCallback, useContext, useMemo, useRef } from "react";

import PropTypes from "prop-types";
import Tooltip from "shared/components/utils/tooltip/Tooltip";
import getEmployeeKey from "features/allocations/components/grid/employee/utils/getEmployeeKey";
import { isBefore } from "date-fns";
import moment from "moment";
import {
    Wrapper,
    PillBorder,
    Pipe,
    AllocatedHours,
    AvailableHours,
    NotAvailableAllocation,
} from "./Pill.styles";

const NULL_CELL_VALUE = "--";

const AVAILABLE_HOURS_BREAKPOINT = 20;
const HOURS_GAP = 4;

const calculateColor = (allocatedHours, availableHours) => {
    let hourGap;
    let check = !(allocatedHours === 0 && availableHours === 0);

    if (allocatedHours === 0 && availableHours === 0) {
        return globalThis.config.colors.blue5;
    }

    hourGap =
        check && availableHours <= AVAILABLE_HOURS_BREAKPOINT
            ? HOURS_GAP
            : HOURS_GAP * 2;

    if (check && allocatedHours === 0) return globalThis.config.colors.blue6;

    if (check && allocatedHours < availableHours)
        return globalThis.config.colors.blue4;

    if (check && allocatedHours === availableHours)
        return globalThis.config.colors.blue5;

    if (
        check &&
        allocatedHours > availableHours &&
        allocatedHours <= availableHours + hourGap
    )
        return globalThis.config.colors.orange3;

    if (check && allocatedHours >= availableHours + (hourGap + 1))
        return globalThis.config.colors.orange2;
};

const PillContent = React.memo(
    ({
        allocatedHours,
        availableHours,
        date,
        employeeData,
        timeOffReasonsAmount,
    }) => {
        const pillColor = useMemo(
            () => calculateColor(allocatedHours, availableHours),
            [allocatedHours, availableHours]
        );

        const buildUnassignedUnallocatedTooltipLabel = () => {
            const dateFrom = moment(date).toDate();
            const departureDate = moment(employeeData.departureDate).toDate();

            switch (true) {
                case timeOffReasonsAmount > 1:
                    return "Statuses: " + timeOffReasonsAmount;
                case !isBefore(departureDate, dateFrom):
                    return "Time off";
                case isBefore(departureDate, dateFrom) &&
                    employeeData.freelance:
                    return "Freelancer Not Working";
                case isBefore(departureDate, dateFrom) &&
                    !employeeData.freelance:
                    return "Employee Departed";
                default:
                    return "";
            }
        };

        const buildAvailableHoursTooltipLabel = () => {
            const dateFrom = moment(date).toDate();
            const departureDate = moment(employeeData.departureDate).toDate();
            const check =
                !isBefore(departureDate, dateFrom) && availableHours !== 0;
            switch (true) {
                case timeOffReasonsAmount > 1:
                    return "Statuses: " + timeOffReasonsAmount;
                case check:
                    return "Hours Available";
                case !check && employeeData.freelance:
                    return "Freelancer Not Working";
                case !check && !employeeData.freelance:
                    return "Employee Departed";
                default:
                    return "";
            }
        };

        return (
            <Wrapper color={pillColor}>
                {allocatedHours !== 0 || availableHours !== 0 ? (
                    <>
                        <Tooltip placement="bottom" title="Hours Allocated">
                            <AllocatedHours>{allocatedHours}</AllocatedHours>
                        </Tooltip>

                        <Pipe />

                        <Tooltip
                            placement="bottom"
                            title={buildAvailableHoursTooltipLabel()}
                        >
                            <AvailableHours>
                                {availableHours === 0
                                    ? NULL_CELL_VALUE
                                    : availableHours}
                            </AvailableHours>
                        </Tooltip>
                    </>
                ) : (
                    <Tooltip
                        placement="bottom"
                        title={buildUnassignedUnallocatedTooltipLabel()}
                    >
                        <NotAvailableAllocation>
                            {NULL_CELL_VALUE}
                        </NotAvailableAllocation>
                    </Tooltip>
                )}
            </Wrapper>
        );
    }
);

PillContent.propTypes = {
    allocatedHours: PropTypes.number,
    availableHours: PropTypes.number,
    date: PropTypes.any,
    employeeData: PropTypes.object,
    timeOffReasonsAmount: PropTypes.number,
};

const Pill = ({
    allocatedHours,
    availableHours,
    date,
    employeeData,
    timeOffReasonsAmount,
}) => {
    const { toggleTooltip } = useContext(GridUIContextActions);
    const { tooltipRequest } = useContext(GridUIContextValue);

    const pillElRef = useRef();

    const onClick = useCallback(
        (event) => {
            event.stopPropagation();

            toggleTooltip({
                employeeData,
                date,
                allocatedHours,
                availableHours,
                pillElRef,
            });
        },
        [allocatedHours, availableHours, date, employeeData, toggleTooltip]
    );

    const isSelected = tooltipRequest
        ? getEmployeeKey(tooltipRequest.employeeData) ===
              getEmployeeKey(employeeData) && tooltipRequest.date === date
        : false;

    return useMemo(
        () => (
            <PillBorder onClick={onClick} selected={isSelected} ref={pillElRef}>
                <PillContent
                    allocatedHours={allocatedHours}
                    availableHours={availableHours}
                    timeOffReasonsAmount={timeOffReasonsAmount}
                    date={date}
                    employeeData={employeeData}
                />
            </PillBorder>
        ),
        [allocatedHours, availableHours, isSelected, onClick, date, employeeData, timeOffReasonsAmount]
    );
};

Pill.defaultProps = {
    allocatedHours: 0,
    availableHours: 0,
};

Pill.propTypes = {
    allocatedHours: PropTypes.number,
    availableHours: PropTypes.number,
    date: PropTypes.any,
    employeeData: PropTypes.object,
    timeOffReasonsAmount: PropTypes.number,
};

export default Pill;
